import React, { FC } from 'react';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@mui/material';
import Button from '@app/components/Button';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem from '@app/components/inputs/DropdownItem';
import COUNTRIES, { getCountryName } from '@app/domain/countries';
import { formatCountryDisplay, parseLegalIdentifierString, resolveCountryCode } from '../NewVerificationTab.utils';
import { useTranslation } from 'react-i18next';
import { DataPointType, EvidenceItem } from '../NewVerificationTab.data';
import { MODAL_LABELS, NOT_RELEVANT } from '../NewVerificationTab.constants';
import { LegalEntityIdType } from '@mortee/domain/vaildatedPayeeManagement';
import Loadable from '@app/utils/Loadable';
import { validateSwift } from '../NewVerificationTab.utils';
import useBankNameBySwiftCode from '../hooks/useBankNameBySwiftCode';
import useBankNameByCountryCodeAndBankCode from '../hooks/useBankNameByCountryCodeAndBankCode';

interface EditModalProps {
  isOpen: boolean;
  currentValue: string;
  dataPointType: DataPointType | null;
  dataPoint?: React.ReactNode;
  vendorValue?: string;
  evidenceValue?: string;
  evidenceItems?: EvidenceItem[];
  vendorItems?: EvidenceItem[];
  columnType: 'manualProcess' | 'finalReview' | null;
  /** Background color to tint the value input with, matching the related cell's status color. */
  inputBackgroundColor?: string;
  /** True when the row's Auto Matching result is NotRelevant — defaults the checkbox when the value is empty. */
  autoMatchIsNotRelevant?: boolean;
  /** Country code presented in the row's table's Account Country row — keys the bank-code bank-name hint. */
  accountCountryCode?: string;
  taxIdTypes?: Loadable<LegalEntityIdType[]>;
  /** Country codes for which a type (by its id) is configured as a Semi-Strong LEI. */
  semiStrongCountryCodesByTypeId?: Record<string, string[]>;
  onSave: (value: string | undefined) => void;
  onCancel: () => void;
}

export const EditModal: FC<EditModalProps> = ({
  isOpen,
  currentValue,
  dataPointType,
  dataPoint,
  vendorValue,
  evidenceValue,
  evidenceItems,
  vendorItems,
  columnType,
  inputBackgroundColor,
  autoMatchIsNotRelevant,
  accountCountryCode,
  taxIdTypes,
  semiStrongCountryCodesByTypeId,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const isLegalIdentifierType = dataPointType === 'legalIdentifiers';
  const isCountryType = dataPointType === 'country' || dataPointType === 'accountCountry';

  // Determine if this field should show the "Marked as Not Relevant" checkbox
  const dataPointString = typeof dataPoint === 'string' ? dataPoint : String(dataPoint || '');
  const isAdditionalName =
    dataPointType === 'name' && (dataPointString === 'Additional Name' || dataPointString.includes('Additional Name'));
  const isAdditionalAddress =
    dataPointType === 'address' && (dataPointString === 'Additional Address' || dataPointString.includes('Additional Address'));
  const isAdditionalBankCode =
    dataPointType === 'bankCode' && dataPointString !== 'Bank Code' && dataPointString.includes('Bank Code');

  // Show "Marked as Not Relevant" for both Manual Processing and Final Review, for: Additional Name,
  // Additional Address, Legal Identifiers, Bank Code, Additional Bank Code, Branch Code, Swift Code,
  // Account Number, IBAN, For Further Credit
  const shouldShowNotRelevantCheckbox =
    (columnType === 'manualProcess' || columnType === 'finalReview') &&
    ((dataPointType === 'bankCode' && (dataPointString === 'Bank Code' || isAdditionalBankCode)) ||
      dataPointType === 'branchCode' ||
      dataPointType === 'swiftCode' ||
      dataPointType === 'accountNumber' ||
      dataPointType === 'iban' ||
      dataPointType === 'forFurtherCredit' ||
      dataPointType === 'clabe' ||
      dataPointType === 'legalIdentifiers' ||
      isAdditionalName ||
      isAdditionalAddress);

  const [isNotRelevant, setIsNotRelevant] = React.useState(false);

  // Parse a formatted "value - type (country)" string back into its parts. Delegates to the shared
  // parseLegalIdentifierString, which peels the trailing "(country)" and splits the type from the
  // RIGHT — so an identifier value that itself contains " - " (e.g. "30-71234567-9 - VAT (AR)") is
  // kept whole instead of being split on its first hyphen. Only typeName is needed here (the dialog
  // edits by type name), so no LEI type list is required to resolve typeId.
  const parseTaxIdValue = (val: string): { value: string; type: string; country: string } => {
    const parsed = parseLegalIdentifierString(val, []);
    if (!parsed) return { value: '', type: '', country: '' };
    return { value: parsed.value, type: parsed.typeName ?? '', country: parsed.countryCode ?? '' };
  };

  const parsedLegalId = isLegalIdentifierType ? parseTaxIdValue(currentValue) : null;

  const [value, setValue] = React.useState(currentValue);
  const [taxIdValue, setTaxIdValue] = React.useState(parsedLegalId?.value || '');
  const [taxIdType, setTaxIdType] = React.useState(parsedLegalId?.type || '');
  const [taxIdCountry, setTaxIdCountry] = React.useState(parsedLegalId?.country || '');
  const [dropdownResetKey, setDropdownResetKey] = React.useState(0);
  const taxIdCountryDropdownRef = React.useRef<HTMLDivElement>(null);
  const countryDropdownRef = React.useRef<HTMLDivElement>(null);

  // Reset dropdowns when modal opens and ensure they're closed
  React.useEffect(() => {
    if (isOpen) {
      setDropdownResetKey((prev) => prev + 1);
      // Blur any inputs in the dropdown containers to prevent them from opening
      setTimeout(() => {
        [taxIdCountryDropdownRef.current, countryDropdownRef.current].forEach((ref) => {
          if (ref) {
            const input = ref.querySelector('input');
            if (input && document.activeElement === input) {
              input.blur();
            }
          }
        });
      }, 50);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isLegalIdentifierType) {
      const parsed = parseTaxIdValue(currentValue);
      setTaxIdValue(parsed.value);
      setTaxIdType(parsed.type);
      setTaxIdCountry(parsed.country);
    } else if (isCountryType) {
      // Handle "CODE - Name", plain country name, or bare code
      setValue(resolveCountryCode(currentValue));
    } else {
      // A value that was explicitly marked Not relevant is represented by the checkbox, not by text
      // in the input — keep the input blank in that case.
      setValue(currentValue === NOT_RELEVANT ? '' : currentValue);
    }
    // Default the "Not Relevant" checkbox to checked when the value was explicitly marked Not
    // relevant, OR when the field is empty and Auto Matching already determined it's not relevant —
    // in that case the operator is confirming the auto result rather than re-deriving it. Any other
    // empty value (e.g. auto-matching Unknown, or nothing entered yet) leaves the checkbox unchecked
    // so the user isn't defaulted into discarding the field.
    setIsNotRelevant(
      shouldShowNotRelevantCheckbox && (currentValue === NOT_RELEVANT || (!currentValue && !!autoMatchIsNotRelevant)),
    );
  }, [currentValue, isCountryType, shouldShowNotRelevantCheckbox, isLegalIdentifierType, autoMatchIsNotRelevant]);

  const showPresets =
    (columnType === 'manualProcess' || columnType === 'finalReview') &&
    (dataPointType === 'name' ||
      dataPointType === 'address' ||
      dataPointType === 'country' ||
      dataPointType === 'accountCountry' ||
      dataPointType === 'legalIdentifiers' ||
      dataPointType === 'bankName' ||
      dataPointType === 'bankCode' ||
      dataPointType === 'branchCode' ||
      dataPointType === 'iban' ||
      dataPointType === 'swiftCode' ||
      dataPointType === 'accountNumber' ||
      dataPointType === 'forFurtherCredit' ||
      dataPointType === 'clabe') &&
    (vendorValue || evidenceValue || (vendorItems !== undefined && vendorItems.length > 0));

  // Selecting a preset only populates the dialog's fields — it does not save or close. The user
  // then reviews the value and clicks Save (or Cancel). For country types, `value` holds the country
  // code (handleSave formats it on save); for legal identifiers, the parsed parts fill the sub-fields.
  const applyPreset = (presetValue: string, legalId?: EvidenceItem['legalId']): void => {
    if (isLegalIdentifierType) {
      // Prefer the original, unparsed parts when the preset carries them (avoids mis-splitting a
      // value that itself contains " - ", e.g. "30-71234567-9 - VAT (AR)"). Fall back to parsing the
      // formatted string only for presets that don't provide the structured parts.
      if (legalId) {
        setTaxIdValue(legalId.value);
        setTaxIdType(legalId.typeName ?? '');
        setTaxIdCountry(legalId.countryCode ?? '');
      } else {
        const parsed = parseTaxIdValue(presetValue);
        setTaxIdValue(parsed.value);
        setTaxIdType(parsed.type);
        setTaxIdCountry(parsed.country);
      }
    } else if (isCountryType) {
      // Presets carry the formatted "CODE - Name" display; resolve back to the bare code (same as
      // open-time seeding) so handleSave formats it exactly once instead of doubling it.
      setValue(resolveCountryCode(presetValue));
    } else {
      setValue(presetValue);
    }
  };

  // Clears all three Legal Identifier fields. Needed because a partially-filled identifier cannot be
  // removed by blanking the value alone (type/country would remain), so this resets value + type +
  // country together. Bumping dropdownResetKey remounts the country dropdown so its UI clears too.
  const handleResetLegalId = (): void => {
    setTaxIdValue('');
    setTaxIdType('');
    setTaxIdCountry('');
    setDropdownResetKey((prev) => prev + 1);
  };

  // Toggling "Not Relevant" for a Legal Identifier clears all three sub-fields when checked so the
  // dialog visibly reflects that the identifier is being discarded (they are also disabled). The
  // value/type/country are ignored on save anyway — handleSave emits undefined when Not Relevant.
  const handleLegalIdNotRelevantChange = (checked: boolean): void => {
    setIsNotRelevant(checked);
    if (checked) {
      handleResetLegalId();
    }
  };

  // Read-only indication: whether the currently-selected type + country is configured as a
  // Semi-Strong LEI. The dialog holds the type as its name and the country as its code, so resolve
  // the selected type's id from the loaded list (by name), then look it up in
  // semiStrongCountryCodesByTypeId and check whether the selected country is included. Derived from
  // state, so it recomputes as the user changes the type or country (unchecking when the combination
  // is no longer semi-strong).
  const selectedTypeId = taxIdTypes?.resolve(
    (types) => types.find((t) => t.name === taxIdType)?.id,
    () => undefined,
  );
  const isSemiStrongLei =
    isLegalIdentifierType &&
    !!taxIdType &&
    !!taxIdCountry &&
    !!selectedTypeId &&
    !!semiStrongCountryCodesByTypeId?.[selectedTypeId]?.includes(taxIdCountry);

  // A legal identifier is saved by its type's id (selectedTypeId), so a value with no type - or with a
  // preset type name the loaded list doesn't know - would reach the server with typeId null. Save stays
  // blocked until a listed type is chosen; while the list is still loading there is nothing to choose
  // from yet, so Save is blocked without the hint. Bypassed when the identifier is marked Not Relevant
  // (nothing is saved) or has no value.
  const legalIdNeedsType =
    isLegalIdentifierType && !(shouldShowNotRelevantCheckbox && isNotRelevant) && !!taxIdValue.trim() && !selectedTypeId;
  const legalIdTypeError = legalIdNeedsType && !!taxIdTypes?.isResolved() ? 'Select a type from the list' : null;

  // SWIFT validation (only for the swiftCode field). Bypassed when the field is marked Not Relevant
  // — the input is then disabled and empty. Recomputed on every keystroke from the current value.
  const isSwiftType = dataPointType === 'swiftCode';
  const swiftError = isSwiftType && !(shouldShowNotRelevantCheckbox && isNotRelevant) ? validateSwift(value) : null;

  // DB bank name for the entered SWIFT. Only queried for swiftCode fields that aren't marked Not
  // Relevant; the hook itself gates on a valid full SWIFT and debounces. undefined => no-op.
  const swiftForBankName = isSwiftType && !(shouldShowNotRelevantCheckbox && isNotRelevant) ? value : undefined;
  const bankNameLoadable = useBankNameBySwiftCode(swiftForBankName);
  const swiftBankName = bankNameLoadable.resolve(
    (name) => name,
    () => null,
  );

  // DB bank name for the entered bank code — same hint as the SWIFT one, resolved by the table's
  // account country + the code. Only for bankCode fields that aren't marked Not Relevant.
  const isBankCodeType = dataPointType === 'bankCode';
  const bankCodeForBankName = isBankCodeType && !(shouldShowNotRelevantCheckbox && isNotRelevant) ? value : undefined;
  const bankCodeBankNameLoadable = useBankNameByCountryCodeAndBankCode(accountCountryCode, bankCodeForBankName);
  const bankCodeBankName = bankCodeBankNameLoadable.resolve(
    (name) => name,
    () => null,
  );

  const handleSave = (): void => {
    // If "Not Relevant" is checked, save undefined
    if (shouldShowNotRelevantCheckbox && isNotRelevant) {
      onSave(undefined);
      return;
    }

    // Never save an invalid SWIFT or a typeless legal identifier (belt-and-suspenders — the Save
    // button is also disabled).
    if (swiftError || legalIdNeedsType) {
      return;
    }

    if (isLegalIdentifierType) {
      // Canonical format is "value - type (COUNTRYCODE)" — the country CODE (uppercased), matching
      // formatLegalEntityIdentifier and what parseLegalIdentifierString expects on the way back.
      // Emitting the country name here produced "(Argentina)" instead of "(AR)" and broke round-trip.
      let formattedValue = taxIdValue;
      if (taxIdType) {
        formattedValue += ` - ${taxIdType}`;
      }
      if (taxIdCountry) {
        formattedValue += ` (${taxIdCountry.toUpperCase()})`;
      }
      onSave(formattedValue);
    } else if (isCountryType) {
      onSave(value ? formatCountryDisplay(value) : '');
    } else if (isSwiftType) {
      // SWIFT codes are stored uppercased (mirrors the old SVM form's upper-case-on-blur). Trim
      // first so the saved value matches what was validated.
      const trimmed = value.trim();
      onSave(trimmed ? trimmed.toUpperCase() : value);
    } else {
      onSave(value);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open): void => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent
        className='max-h-[85vh] overflow-y-auto'
        style={{
          fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        }}
      >
        <div className='py-4 min-w-0 max-w-full overflow-x-hidden space-y-4'>
          {isLegalIdentifierType ? (
            <>
              <div className='flex items-center' style={{ width: 'fit-content' }}>
                <Checkbox id='semi-strong-lei-checkbox' checked={isSemiStrongLei} disabled readOnly />
                <label htmlFor='semi-strong-lei-checkbox' className='text-sm'>
                  Strong LEI
                </label>
              </div>
              <div>
                <label className='block text-sm font-medium mb-2'>Value</label>
                <Input
                  type='text'
                  data-testid='verification-edit-modal-taxid-value'
                  value={taxIdValue}
                  onChange={(e): void => setTaxIdValue(e.target.value)}
                  autoFocus
                  placeholder='Enter value'
                  disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                  style={inputBackgroundColor ? { backgroundColor: inputBackgroundColor } : undefined}
                />
              </div>
              <div>
                <label className='block text-sm font-medium mb-2'>Type</label>
                <div onClick={(e): void => e.stopPropagation()}>
                  {taxIdTypes?.resolve(
                    (types) => (
                      <NakedDropdown
                        accessibilityLabel='Tax ID Type'
                        name='edit-taxid-type-dropdown'
                        dataTestId='edit-taxid-type-dropdown'
                        placeholder='Select a type...'
                        value={taxIdType ? types.find((t) => t.name === taxIdType)?.name || taxIdType : undefined}
                        onChange={(newValue): void => {
                          if (newValue !== undefined && newValue !== null) {
                            setTaxIdType(newValue);
                          }
                        }}
                        isSearchable
                        disablePortal={true}
                        disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                      >
                        {types.map((type) => (
                          <DropdownItem key={type.id} value={type.name} textWhenSelected={type.name} keywords={[type.name]}>
                            <div>{type.name}</div>
                          </DropdownItem>
                        ))}
                      </NakedDropdown>
                    ),
                    () => (
                      <NakedDropdown
                        accessibilityLabel='Tax ID Type'
                        name='edit-taxid-type-dropdown'
                        dataTestId='edit-taxid-type-dropdown'
                        placeholder='Loading types...'
                        disabled
                        children={[]}
                      />
                    ),
                  )}
                </div>
                {legalIdTypeError && (
                  <div id='edit-legal-id-type-error' className='text-sm text-red-600 mt-1'>
                    {legalIdTypeError}
                  </div>
                )}
              </div>
              <div>
                <label className='block text-sm font-medium mb-2'>Country</label>
                <div ref={taxIdCountryDropdownRef} onClick={(e): void => e.stopPropagation()}>
                  <NakedDropdown
                    key={`taxid-country-${dropdownResetKey}`}
                    accessibilityLabel={t('general.accessibility.country')}
                    name='edit-taxid-country-dropdown'
                    dataTestId='edit-taxid-country-dropdown'
                    placeholder='Select a country...'
                    value={taxIdCountry || undefined}
                    onChange={(newValue): void => {
                      if (newValue !== undefined && newValue !== null) {
                        setTaxIdCountry(newValue);
                      }
                    }}
                    isSearchable
                    disablePortal={true}
                    disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                  >
                    {Object.entries(COUNTRIES)
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([countryCode, country]) => {
                        const countryName = getCountryName(countryCode) || country.name;
                        const display = `${countryCode} - ${countryName}`;
                        return (
                          <DropdownItem
                            key={countryCode}
                            value={countryCode}
                            textWhenSelected={display}
                            keywords={[countryName, countryCode]}
                          >
                            <div>{display}</div>
                          </DropdownItem>
                        );
                      })}
                  </NakedDropdown>
                </div>
              </div>
              {shouldShowNotRelevantCheckbox && (
                <div
                  className='flex items-center mt-2'
                  onClick={(): void => handleLegalIdNotRelevantChange(!isNotRelevant)}
                  style={{ cursor: 'pointer', width: 'fit-content' }}
                >
                  <Checkbox
                    id='not-relevant-checkbox-legal-id'
                    checked={isNotRelevant}
                    onClick={(e): void => e.stopPropagation()}
                    onChange={(e): void => handleLegalIdNotRelevantChange(e.target.checked)}
                  />
                  <label htmlFor='not-relevant-checkbox-legal-id' style={{ cursor: 'pointer' }}>
                    Not Relevant
                  </label>
                </div>
              )}
            </>
          ) : (
            <>
              {!isCountryType && <label className='block text-sm font-medium mb-2'>{MODAL_LABELS.VALUE}</label>}
              {isCountryType ? (
                <div
                  ref={countryDropdownRef}
                  onClick={(e): void => e.stopPropagation()}
                  className='border border-gray-300 rounded-md p-2'
                >
                  <NakedDropdown
                    key={`country-${dropdownResetKey}`}
                    accessibilityLabel={t('general.accessibility.country')}
                    name='edit-country-dropdown'
                    dataTestId='edit-country-dropdown'
                    placeholder='Select a country...'
                    value={value || undefined}
                    onChange={(newValue): void => {
                      if (newValue !== undefined && newValue !== null) {
                        setValue(newValue);
                      }
                    }}
                    isSearchable
                    disablePortal={true}
                  >
                    {Object.entries(COUNTRIES)
                      .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                      .map(([countryCode, country]) => {
                        const countryName = getCountryName(countryCode) || country.name;
                        const display = `${countryCode} - ${countryName}`;
                        return (
                          <DropdownItem
                            key={countryCode}
                            value={countryCode}
                            textWhenSelected={display}
                            keywords={[countryName, countryCode]}
                          >
                            <div>{display}</div>
                          </DropdownItem>
                        );
                      })}
                  </NakedDropdown>
                </div>
              ) : (
                <>
                  <Input
                    type='text'
                    data-testid='verification-edit-modal-value'
                    value={value}
                    onChange={(e): void => setValue(e.target.value)}
                    autoFocus
                    disabled={shouldShowNotRelevantCheckbox && isNotRelevant}
                    style={inputBackgroundColor ? { backgroundColor: inputBackgroundColor } : undefined}
                  />
                  {swiftError && (
                    <div id='edit-swift-error' className='text-sm text-red-600 mt-1'>
                      {swiftError}
                    </div>
                  )}
                  {isSwiftType && !swiftError && swiftBankName && (
                    <div id='edit-swift-bank-name' className='text-sm text-gray-600 mt-1'>
                      Bank Name (DB) - {swiftBankName}
                    </div>
                  )}
                  {isBankCodeType && bankCodeBankName && (
                    <div id='edit-bank-code-bank-name' className='text-sm text-gray-600 mt-1'>
                      Bank Name (DB) - {bankCodeBankName}
                    </div>
                  )}
                  {shouldShowNotRelevantCheckbox && (
                    <div
                      className='flex items-center mt-2'
                      onClick={(): void => setIsNotRelevant(!isNotRelevant)}
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                    >
                      <Checkbox
                        id='not-relevant-checkbox'
                        checked={isNotRelevant}
                        onClick={(e): void => e.stopPropagation()}
                        onChange={(e): void => setIsNotRelevant(e.target.checked)}
                      />
                      <label htmlFor='not-relevant-checkbox' style={{ cursor: 'pointer' }}>
                        Not Relevant
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {showPresets && (
            <div className='mt-4 space-y-2 min-w-0'>
              <label className='block text-xs font-medium text-gray-600 mb-2'>{MODAL_LABELS.PRESETS}:</label>
              <div className='flex flex-col gap-2 min-w-0'>
                {vendorItems && vendorItems.length > 0
                  ? vendorItems.map((item, idx) => {
                      const displayValue = item.value || '';
                      const itemLabel = [item.source, item.evidenceType].filter(Boolean).join(' · ');
                      return (
                        <Button
                          key={`preset-vendor-${idx}`}
                          id={`preset-vendor-${idx}`}
                          appearance='outline'
                          colorScheme='primary'
                          onClick={(): void => applyPreset(displayValue, item.legalId)}
                          className='w-full text-left min-w-0 overflow-hidden'
                        >
                          <span className='block break-words min-w-0'>
                            <span className='font-medium'>{MODAL_LABELS.VENDOR_REGISTRATION}</span>{' '}
                            {isCountryType
                              ? getCountryName(displayValue) || COUNTRIES[displayValue]?.name || displayValue
                              : displayValue}
                            {itemLabel && <span className='text-xs text-gray-500 ml-1'>{itemLabel}</span>}
                          </span>
                        </Button>
                      );
                    })
                  : vendorValue && (
                      <Button
                        id='preset-vendor'
                        appearance='outline'
                        colorScheme='primary'
                        onClick={(): void => applyPreset(vendorValue)}
                        className='w-full text-left min-w-0 overflow-hidden'
                      >
                        <span className='block break-words min-w-0'>
                          <span className='font-medium'>{MODAL_LABELS.VENDOR_REGISTRATION}</span>{' '}
                          {isCountryType
                            ? getCountryName(vendorValue) || COUNTRIES[vendorValue]?.name || vendorValue
                            : vendorValue}
                        </span>
                      </Button>
                    )}
                {evidenceItems && evidenceItems.length > 0
                  ? evidenceItems.map((item, idx) => {
                      const displayValue = item.value || '';
                      const itemLabel = [item.source, item.evidenceType].filter(Boolean).join(' · ');
                      return (
                        <Button
                          key={`preset-evidence-${idx}`}
                          id={`preset-evidence-${idx}`}
                          appearance='outline'
                          colorScheme='primary'
                          onClick={(): void => applyPreset(displayValue, item.legalId)}
                          className='w-full text-left min-w-0 overflow-hidden'
                        >
                          <span className='block break-words min-w-0'>
                            <span className='font-medium'>{MODAL_LABELS.EVIDENCE_ENRICHMENT}</span> {displayValue}
                            {itemLabel && <span className='text-xs text-gray-500 ml-1'>{itemLabel}</span>}
                          </span>
                        </Button>
                      );
                    })
                  : evidenceValue && (
                      <Button
                        id='preset-evidence'
                        appearance='outline'
                        colorScheme='primary'
                        onClick={(): void => applyPreset(evidenceValue)}
                        className='w-full text-left min-w-0 overflow-hidden'
                      >
                        <span className='block break-words min-w-0'>
                          <span className='font-medium'>{MODAL_LABELS.EVIDENCE_ENRICHMENT}</span>{' '}
                          {isCountryType
                            ? getCountryName(evidenceValue) || COUNTRIES[evidenceValue]?.name || evidenceValue
                            : evidenceValue}
                        </span>
                      </Button>
                    )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          {isLegalIdentifierType && (
            <Button id='modal-reset-legal-id' appearance='text' colorScheme='primary' onClick={handleResetLegalId}>
              {MODAL_LABELS.RESET}
            </Button>
          )}
          <Button id='modal-cancel' appearance='outline' colorScheme='primary' onClick={onCancel}>
            {MODAL_LABELS.CANCEL}
          </Button>
          <Button id='modal-save' colorScheme='primary' onClick={handleSave} disabled={!!swiftError || legalIdNeedsType}>
            {MODAL_LABELS.SAVE}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
